import { IProposal } from '@shared/models/proposals.model';
import { SbefGroupCode } from '../../construction/sbef.model';
import { ElementCategoryID } from '../../models/element-categories.model';
import { ElementPropertyName } from '../../models/element-property.model';
import { Baseline, IElement, OneOfElements } from '../../models/project.model';
import { includesSome, isOneOf } from '../array.helpers';
import { getElementCategoryId } from '../element-category.helpers';
import { getElementPropertyResolvedCountByNameOrId } from '../element-property.helpers';
import { isElementActiveInProposal } from '../proposal.helpers';
import { forEachElement, isElement } from '../recursive-element.helpers';
import { getSbefGroupCodeFromElementOrRecipe } from '../sbef.helpers';
import { BUILDING_ENVELOPE_TYPE } from './energy.models';

const BUILDING_ENVELOPE_CATEGORY_IDS = [
  ElementCategoryID.Slab,
  ElementCategoryID.Wall,
  ElementCategoryID.WindowsDoors,
  // ElementCategoryID.ROOF,
];

/**
 * Get all elements that are building envelope elements. IE, Slab & Wall elements with property "u"
 * @param version
 * @param forProposal When provided, envelope is filtered by this proposal (for per-proposal heat demand).
 * @returns
 */
export const getBuildingEnvelopeElements = (
  version: Baseline,
  forProposal?: IProposal,
): IElement[] => {
  const envelopeElements: IElement[] = [];

  forEachElement(version, (e, path) => {
    if (!isElement(e)) {
      return false;
    }
    // If not an element or a parent of this element is already added
    if (
      isBuildingEnvelopeElement(e) &&
      isElementActiveInProposal(forProposal, path, e) &&
      !includesSome(envelopeElements, ...path) // Only add the element at the highest level (and not their children)
    ) {
      envelopeElements.push(e);
    }
  });

  return envelopeElements;
};

/**
 * Check if an element is a building envelope element and should be included in the heating demand calculation
 * @param element
 * @returns
 */
export const isBuildingEnvelopeElement = (element?: OneOfElements): boolean => {
  const categoryId = getElementCategoryId(element);
  if (isOneOf(BUILDING_ENVELOPE_CATEGORY_IDS, categoryId)) {
    // Windows currently can't have thermal transmittance so the hack is to put them inside a wall element
    // And we always use the element for solar insolation calculation
    if (categoryId === ElementCategoryID.WindowsDoors) {
      return true;
    }

    return (
      getElementPropertyResolvedCountByNameOrId(
        element,
        ElementPropertyName.ThermalTransmittance,
        0,
      ) > 0
    );
  }

  return false;
};

/**
 * Get the type of building envelope element
 * @param element
 * @returns
 */
export const getBuildingEnvelopeType = (
  element: OneOfElements,
): BUILDING_ENVELOPE_TYPE | undefined => {
  if (!isBuildingEnvelopeElement(element)) {
    return;
  }
  const categoryId = getElementCategoryId(element);
  if (categoryId === ElementCategoryID.Slab) {
    const sbefGroupId = getSbefGroupCodeFromElementOrRecipe(element);
    if (sbefGroupId === SbefGroupCode.Ground) {
      return BUILDING_ENVELOPE_TYPE.FOUNDATION;
    }
    if (sbefGroupId === SbefGroupCode.OuterRoof) {
      return BUILDING_ENVELOPE_TYPE.ROOF;
    }
    return BUILDING_ENVELOPE_TYPE.WALL;
  } else if (categoryId === ElementCategoryID.Wall) {
    return BUILDING_ENVELOPE_TYPE.WALL;
  }
};
